import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, Pencil } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { getShowHolidays, updateHolidaysData } from "@/Api/holidays";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin, CreateLoaderInput } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { HOLIDAY_TYPE_CONST } from "@/constants/HolidayTypeConst";

const HOLIDAY_TYPES = [
  { value: HOLIDAY_TYPE_CONST.NATIONAL, label: "Nacional" },
  { value: HOLIDAY_TYPE_CONST.STATE, label: "Estadual" },
  { value: HOLIDAY_TYPE_CONST.MUNICIPAL, label: "Municipal" },
  { value: HOLIDAY_TYPE_CONST.OPTIONAL, label: "Facultativo" },
];

const schema = z.object({
  name: z.string({ message: "*requerido" }).min(1, "*requerido"),
  date: z.string({ message: "*requerido" }).min(1, "*requerido"),
  holiday_type_id: z.string({ message: "*requerido" }).min(1, "*requerido"),
  is_recurring_yearly: z.boolean().optional(),
  state: z.string().optional(),
  city: z.string().optional(),
});

type SchemaType = z.infer<typeof schema>;

export const UpdateBtn = ({
  updateFront,
  uuid,
}: {
  updateFront: (response: any) => void;
  uuid: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
  });

  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");
  const formWatch = form.watch();

  useEffect(() => {
    const fetchData = async () => {
      if (isOpen) {
        setIsLoading(true);
        try {
          const { data: responseApi } = await getShowHolidays(uuid);
          const response = responseApi?.response;

          form.setValue("name", response?.name || "");
          form.setValue("date", response?.date?.slice(0, 10) || "");
          form.setValue(
            "holiday_type_id",
            String(response?.holiday_type_id || HOLIDAY_TYPE_CONST.NATIONAL)
          );
          form.setValue(
            "is_recurring_yearly",
            !!response?.is_recurring_yearly
          );
          form.setValue("state", response?.state || "");
          form.setValue("city", response?.city || "");
        } catch (error) {
          console.error(error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, [isOpen, uuid, form]);

  const formSubmit = async (data: SchemaType) => {
    setFormState("loading");
    const payload = {
      ...data,
      state: data.state || null,
      city: data.city || null,
    };

    const { data: apiResponse, ok } = await updateHolidaysData(uuid, payload);
    if (!ok) {
      setFormState("error");

      setTimeout(() => {
        setFormState("default");
      }, 1000);
      setIsOpen(false);
      return;
    }

    updateFront(apiResponse?.response);
    setFormState("default");
    setIsOpen(false);
    toast({
      description: apiResponse?.message,
      action: <Check color="#1edc2b" />,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
          onClick={() => setIsOpen(true)}
        >
          <Pencil className="size-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Editar Feriado
          </DialogDescription>
          <DialogTitle className="text-2xl">Informações Básicas</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <CreateLoaderInput lines={5} />
        ) : (
          <Form
            onSubmit={form.handleSubmit(formSubmit)}
            {...form}
            className="space-y-3"
          >
            <Form.Row>
              <Form.Field name="name">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Nome <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Input {...field} placeholder="Insira um nome" />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>

            <Form.Row>
              <Form.Field name="date">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Data <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Input {...field} type="date" />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
              <Form.Field name="holiday_type_id">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Tipo <Form.Message />
                    </Form.Label>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        {HOLIDAY_TYPES.map((type) => (
                          <SelectItem
                            key={type.value}
                            value={type.value.toString()}
                          >
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>

            <Form.Row>
              <Form.Field name="state">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>UF</Form.Label>
                    <Form.Control>
                      <Input {...field} placeholder="Ex: SP" maxLength={2} />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
              <Form.Field name="city">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>Cidade</Form.Label>
                    <Form.Control>
                      <Input {...field} placeholder="Insira uma cidade" />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>

            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={formWatch?.is_recurring_yearly}
                onCheckedChange={(checked) =>
                  form.setValue("is_recurring_yearly", !!checked)
                }
              />
              Recorrente todo ano
            </label>

            <DialogFooter>
              <Button
                type="submit"
                className={cn("w-full", colors.bg_primary)}
                disabled={formState !== "default"}
              >
                {formState === "loading" && (
                  <>
                    Atualizando <LoaderSpin />
                  </>
                )}
                {formState === "default" && "Atualizar"}
                {formState === "error" && "Erro ao atualizar"}
              </Button>
            </DialogFooter>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
